import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from './ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Link2, Package, Loader2, Check, X, ExternalLink, 
  ShoppingCart, Tag, Facebook, Zap, AlertCircle
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Platform icons and colors
const PLATFORM_CONFIG = {
  walmart: { 
    name: 'Walmart', 
    color: '#0071CE', 
    bgColor: 'bg-blue-500',
    icon: ShoppingCart,
    placeholder: 'https://www.walmart.com/ip/Product-Name/12345678'
  },
  amazon: { 
    name: 'Amazon', 
    color: '#FF9900', 
    bgColor: 'bg-orange-500',
    icon: Package,
    placeholder: 'https://www.amazon.com/dp/B08N5WRWNW'
  },
  temu: { 
    name: 'Temu', 
    color: '#FB7701', 
    bgColor: 'bg-orange-600',
    icon: Tag,
    placeholder: 'https://www.temu.com/product-name-g-123456.html'
  },
  facebook: { 
    name: 'Facebook', 
    color: '#1877F2', 
    bgColor: 'bg-blue-600',
    icon: Facebook,
    placeholder: 'https://www.facebook.com/marketplace/item/123456789'
  }
};

const categories = [
  { value: 'auto_parts', label: 'Auto Parts' },
  { value: 'tools', label: 'Tools & Equipment' },
  { value: 'vehicles', label: 'Vehicles' },
  { value: 'accessories', label: 'Accessories' },
  { value: 'other', label: 'Other' }
];

/**
 * QuickAddButton - Button that opens the Quick Add dialog
 */
export function QuickAddButton({ onProductAdded, className = '' }) {
  const [open, setOpen] = useState(false);
  
  const handleSuccess = (product) => {
    setOpen(false);
    if (onProductAdded) onProductAdded(product);
  };
  
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button 
          variant="outline" 
          className={`gap-2 ${className}`}
          data-testid="quick-add-button"
        >
          <Zap className="w-4 h-4" />
          Quick Add
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg mx-4 sm:mx-auto">
        <QuickAddDialog onSuccess={handleSuccess} onClose={() => setOpen(false)} />
      </DialogContent>
    </Dialog>
  );
}

/**
 * QuickAddDialog - The main dialog content for importing products
 */
export function QuickAddDialog({ onSuccess, onClose }) {
  const { user, getAuthHeaders } = useAuth();
  const [platforms, setPlatforms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [importing, setImporting] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [url, setUrl] = useState('');
  const [selectedPlatform, setSelectedPlatform] = useState('');
  const [category, setCategory] = useState('other');
  const [customPrice, setCustomPrice] = useState('');
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState('');
  
  useEffect(() => {
    fetchPlatforms();
  }, []);
  
  const fetchPlatforms = async () => {
    try {
      const response = await axios.get(`${API}/quick-add/platforms`, {
        headers: getAuthHeaders()
      });
      setPlatforms(response.data.platforms);
      if (response.data.platforms.length > 0) {
        setSelectedPlatform(response.data.platforms[0].id);
      }
    } catch (err) {
      console.error('Failed to fetch platforms:', err);
    } finally {
      setLoading(false);
    }
  };
  
  const handlePreview = async () => {
    if (!url.trim()) {
      setError('Please enter a product URL');
      return;
    }
    
    setError('');
    setPreviewing(true);
    
    try {
      const response = await axios.post(`${API}/quick-add/preview`, {
        url: url.trim(),
        platform: selectedPlatform,
        category
      }, { headers: getAuthHeaders() });
      
      setPreview(response.data.preview);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to preview product');
    } finally {
      setPreviewing(false);
    }
  };
  
  const handleImport = async () => {
    setError('');
    setImporting(true);
    
    try {
      const response = await axios.post(`${API}/quick-add/import`, {
        url: url.trim(),
        platform: selectedPlatform,
        category,
        custom_price: customPrice ? parseFloat(customPrice) : null
      }, { headers: getAuthHeaders() });
      
      toast.success(`Product imported: ${response.data.title}`);
      if (onSuccess) onSuccess(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to import product');
      toast.error('Import failed');
    } finally {
      setImporting(false);
    }
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
      </div>
    );
  }
  
  return (
    <>
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
          <Zap className="w-5 h-5 text-yellow-500" />
          Quick Add Product
        </DialogTitle>
      </DialogHeader>
      
      <div className="space-y-4 py-4">
        {/* Platform Selection */}
        <div>
          <label className="text-sm font-medium text-gray-700 mb-2 block">
            Import from
          </label>
          <div className="flex flex-wrap gap-2">
            {platforms.map((platform) => {
              const config = PLATFORM_CONFIG[platform.id];
              const Icon = config?.icon || Package;
              return (
                <button
                  key={platform.id}
                  onClick={() => setSelectedPlatform(platform.id)}
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg border-2 transition-all ${
                    selectedPlatform === platform.id
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <Icon className="w-4 h-4" style={{ color: config?.color }} />
                  <span className="text-sm font-medium">{platform.name}</span>
                </button>
              );
            })}
          </div>
          {platforms.length === 1 && (
            <p className="text-xs text-gray-500 mt-2">
              Only Facebook Marketplace is available for customer accounts
            </p>
          )}
        </div>
        
        {/* URL Input */}
        <div>
          <label className="text-sm font-medium text-gray-700 mb-2 block">
            Product URL
          </label>
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Link2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                value={url}
                onChange={(e) => { setUrl(e.target.value); setPreview(null); }}
                placeholder={PLATFORM_CONFIG[selectedPlatform]?.placeholder || 'Paste product URL'}
                className="pl-10"
                data-testid="quick-add-url-input"
              />
            </div>
            <Button
              variant="outline"
              onClick={handlePreview}
              disabled={previewing || !url.trim()}
            >
              {previewing ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Preview'}
            </Button>
          </div>
        </div>
        
        {/* Error Message */}
        {error && (
          <div className="flex items-center gap-2 text-red-600 text-sm bg-red-50 p-3 rounded-lg">
            <AlertCircle className="w-4 h-4" />
            {error}
          </div>
        )}
        
        {/* Preview Card */}
        {preview && (
          <Card className="border-green-200 bg-green-50/50">
            <CardContent className="p-4">
              <div className="flex gap-4">
                {preview.images?.[0] && (
                  <img 
                    src={preview.images[0]} 
                    alt="" 
                    className="w-20 h-20 object-cover rounded-lg bg-white"
                    onError={(e) => e.target.style.display = 'none'}
                  />
                )}
                <div className="flex-1 min-w-0">
                  <h4 className="font-medium text-gray-900 truncate">{preview.title}</h4>
                  <p className="text-lg font-bold text-green-600">${preview.price?.toFixed(2)}</p>
                  <Badge variant="outline" className="mt-1">
                    {preview.condition === 'new' ? 'New' : 'Used'}
                  </Badge>
                </div>
                <Check className="w-5 h-5 text-green-600 flex-shrink-0" />
              </div>
            </CardContent>
          </Card>
        )}
        
        {/* Additional Options (shown after preview) */}
        {preview && (
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-gray-700 mb-2 block">
                Category
              </label>
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {categories.map((cat) => (
                    <SelectItem key={cat.value} value={cat.value}>
                      {cat.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700 mb-2 block">
                Custom Price (optional)
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                <Input
                  type="number"
                  value={customPrice}
                  onChange={(e) => setCustomPrice(e.target.value)}
                  placeholder={preview.price?.toFixed(2)}
                  className="pl-7"
                  step="0.01"
                  min="0"
                />
              </div>
            </div>
          </div>
        )}
      </div>
      
      <DialogFooter className="flex-col sm:flex-row gap-2">
        <Button variant="outline" onClick={onClose} className="w-full sm:w-auto">
          Cancel
        </Button>
        <Button
          onClick={handleImport}
          disabled={!preview || importing}
          className="w-full sm:w-auto bg-green-600 hover:bg-green-700"
          data-testid="quick-add-import-button"
        >
          {importing ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Importing...
            </>
          ) : (
            <>
              <Check className="w-4 h-4 mr-2" />
              Import to Marketplace
            </>
          )}
        </Button>
      </DialogFooter>
    </>
  );
}

/**
 * QuickAddMini - Compact version for mobile/inline use
 */
export function QuickAddMini({ onProductAdded }) {
  const [url, setUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const { getAuthHeaders } = useAuth();
  
  const handleQuickImport = async () => {
    if (!url.trim()) return;
    
    setLoading(true);
    try {
      const response = await axios.post(`${API}/quick-add/import`, {
        url: url.trim(),
        category: 'other'
      }, { headers: getAuthHeaders() });
      
      toast.success(`Imported: ${response.data.title}`);
      setUrl('');
      if (onProductAdded) onProductAdded(response.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Import failed');
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <div className="flex gap-2" data-testid="quick-add-mini">
      <Input
        value={url}
        onChange={(e) => setUrl(e.target.value)}
        placeholder="Paste product URL to quick add..."
        className="flex-1"
        onKeyPress={(e) => e.key === 'Enter' && handleQuickImport()}
      />
      <Button 
        onClick={handleQuickImport} 
        disabled={loading || !url.trim()}
        size="sm"
      >
        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />}
      </Button>
    </div>
  );
}

export default QuickAddButton;
